import { capitalize, ROLE_USER } from "@/lib/utils";
import { useGetUsersList } from "@/services";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form"
import { CURRENT_SHARE, OWNER_NAME, ROLE, TRANSFER_USER_ID, USERS } from "./consts";
import { OWNER, OWNERSHIP_SHARE, USER_ID } from "../consts";
import { FormInput } from "@/components";
import { toast } from "@/components/Toast";

const useRemoveJointOwnerDialog = ({allUsers, selectedUser, onSave}: {allUsers: any, selectedUser: any, onSave: (data: any) => void}) => {
  const [pageNumber, setPageNumber] = useState(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [userOptions, setUserOptions] = useState<any[]>([]);
  
  const form = useForm<any>({
    defaultValues: {
      [TRANSFER_USER_ID]: "",
      [USERS]: []
    }
  })

  const {
    data: userList,
    isLoading: isUserListLoading,
    refetch: refetchUser,
    isRefetching: isRefetchingUser,
  } = useGetUsersList({
    data: {
      filters: [
        {
          field: "role",
          value: ROLE_USER,
          operator: "CONTAINS",
        },
      ],
      pageNumber,
      pageSize: 10
    },
  });
  
  useEffect(() => {
    if (userList?.data) {
      const incomingOptions = userList.data.map((d: any) => ({
        label: `${d?.firstName || ""} ${d?.lastName || ""}`.trim() || d?.username,
        value: d?.userId?.toString()
      }));

      // Check if we reached the last page
      if (incomingOptions.length < 10) {
        setHasMore(false);
      }

      setUserOptions((prevOptions) => {
        // Reset list on first page
        if (pageNumber === 0) return incomingOptions;

        // Deduplicate using a Map/Set on unique ID values
        const existingIds = new Set(prevOptions.map((opt) => opt.value));
        const filteredNew = incomingOptions.filter((opt: any) => !existingIds.has(opt.value));
        
        return [...prevOptions, ...filteredNew];
      });
    }
  }, [userList, pageNumber]);
  
  const loadMoreOptions = () => {
    if (!isUserListLoading && !isRefetchingUser && hasMore) {
      setPageNumber((prev) => prev + 1);
      setTimeout(() => refetchUser(), 100)
    }
  };

  useEffect(() => {
    const usr = allUsers.filter((data: any) => data[USER_ID] != selectedUser[USER_ID])
    form.setValue(USERS, usr);
  }, [allUsers]);

  const users = useWatch({ control: form.control, name: USERS});
  const transferUserId = useWatch({ control: form.control, name: TRANSFER_USER_ID});

  const columns = [
    {
      accessorKey: "name",
      header: "Owner Name",
      accessorFn: (row: any) => String(row?.name || ""),
    },
    {
      accessorKey: "role",
      header: "Role",
      accessorFn: (row: any) => capitalize(row?.role || ""),
    },
    {
      accessorKey: "currentShare",
      header: "Current Share",
      accessorFn: (row: any) => String(`${row?.currentShare}%` || ""),
    },
    {
      accessorKey: "ownershipShare",
      header: "New Share",
      cell: ({row}: any) => {
        return <FormInput 
          name={`${USERS}.${row.index}.${OWNERSHIP_SHARE}`}
          control={form.control}
          label=""
          placeholder="Please enter"
        />
      },
    },
  ]

  const totalShare = useMemo(() => {
    if (!users?.length) return 0;
    
    return users.reduce((sum: number, item: any) => sum + Number(item[OWNERSHIP_SHARE] || 0), 0)
  }, [users]);

  useEffect(() => {
    if (transferUserId) {
      const newOwner = userList?.data?.find((data: any) => data.userId == transferUserId)
      const usr = [...users, {...newOwner, id: users.length + 1, [OWNER_NAME]: newOwner?.name, [ROLE]: OWNER, [CURRENT_SHARE]: "0", [OWNERSHIP_SHARE]: 0}]
      form.setValue(USERS, usr);
      form.setValue(TRANSFER_USER_ID, "");
    }
  }, [transferUserId]);

  const onSubmit = (data: any) => {
    if (totalShare != 100) {
      toast.error("Total share must to 100%");
      return;
    }

    onSave(data[USERS]);
  }

  return {
    form,
    isLoading: isUserListLoading || isRefetchingUser,
    userOptions,
    isUserListLoading,
    hasMore,
    loadMoreOptions,
    users,
    columns,
    totalShare,
    onSubmit
  }
}

export default useRemoveJointOwnerDialog;